import Loading from 'components/common/loading/Loading'
import { formatDate } from 'components/utils/FormatDate'
import { forwardRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { applicationStatusBadgeClass, applicationStatusKey, applicationStatusProgress } from './mySpaceUtils'

export type MyApplicationRow = {
  id: string | number
  property_fk?: string | number | null
  property_title?: string | null
  created_at?: string
  updated_at?: string | null
  application_status?: string
  status?: string
}

type MySpaceApplicationsSectionProps = {
  applications: MyApplicationRow[] | undefined
  isLoading: boolean
  isOwnerView?: boolean
  onWithdraw: (applicationId: string | number) => void
  isWithdrawing: boolean
}

const ClipboardIcon = () => (
  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
  </svg>
)

const MySpaceApplicationsSection = forwardRef<HTMLElement, MySpaceApplicationsSectionProps>(
  ({ applications, isLoading, isOwnerView = false, onWithdraw, isWithdrawing }, ref) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const items = applications ?? []

  return (
    <section
      ref={ref}
      id="my-space-applications"
      className="scroll-mt-24 rounded-2xl border border-gray-100 bg-white p-5 shadow-sm sm:p-6"
    >
      <div className="mb-4 flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-imovPrimary/10 text-imovPrimary">
          <ClipboardIcon />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-imovSecondary">
            {isOwnerView
              ? t('account.mySpace.applicationsTitleOwner')
              : t('account.mySpace.applicationsTitle')}
          </h2>
          <p className="text-sm text-gray-500">
            {isOwnerView
              ? t('account.mySpace.applicationsSubtitleOwner')
              : t('account.mySpace.applicationsSubtitle')}
          </p>
        </div>
      </div>

      {isLoading ? (
        <Loading />
      ) : items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50 px-4 py-10 text-center">
          <p className="text-sm text-gray-600">
            {isOwnerView
              ? t('account.mySpace.noApplicationsOwner')
              : t('account.mySpace.noApplications')}
          </p>
          <button
            type="button"
            onClick={() => navigate('/listings')}
            className="mt-3 inline-flex rounded-xl bg-imovPrimary px-4 py-2 text-sm font-semibold text-white hover:bg-imovPrimaryDark"
          >
            {t('account.mySpace.browseListings')}
          </button>
        </div>
      ) : (
        <ul className="space-y-3">
          {items.map(app => {
            const status = applicationStatusKey(app.application_status ?? app.status)
            const progress = applicationStatusProgress(app.application_status ?? app.status)
            const propertyId = app.property_fk
            const canWithdraw =
              !isOwnerView && status !== 'withdrawn' && status !== 'approved'

            return (
              <li key={String(app.id)} className="rounded-xl bg-gray-50 p-4">
                <div className="flex items-start gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-imovSecondary">
                      {app.property_title || t('account.mySpace.untitledListing')}
                    </p>
                    <p className="mt-1 text-xs text-gray-500">
                      {t('account.mySpace.appliedOn', {
                        date: app.created_at ? formatDate(app.created_at) : '—',
                      })}
                    </p>
                    <p className="text-xs text-gray-400">
                      {t('account.mySpace.applicationRef', { id: String(app.id) })}
                    </p>
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      <span className={applicationStatusBadgeClass(status)}>
                        {t(`account.mySpace.applicationStatus.${status}`)}
                      </span>
                      <span className="text-xs text-gray-400">
                        {t('account.mySpace.lastUpdated', {
                          date: app.updated_at
                            ? formatDate(app.updated_at)
                            : app.created_at
                              ? formatDate(app.created_at)
                              : '—',
                        })}
                      </span>
                    </div>
                    <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-gray-200">
                      <div
                        className={`h-full rounded-full transition-all ${
                          status === 'rejected' ? 'bg-red-400' : 'bg-imovPrimary'
                        }`}
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                    {canWithdraw ? (
                      <button
                        type="button"
                        disabled={isWithdrawing}
                        onClick={() => onWithdraw(app.id)}
                        className="mt-3 text-xs font-medium text-red-600 hover:underline disabled:opacity-50"
                      >
                        {t('account.mySpace.withdrawApplication')}
                      </button>
                    ) : null}
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      if (propertyId) {
                        navigate(
                          isOwnerView
                            ? `/my-listings/${propertyId}/manage`
                            : `/listing/${propertyId}`,
                          {
                            state: { returnTo: '/account' },
                          }
                        )
                      }
                    }}
                    disabled={!propertyId}
                    className="shrink-0 rounded-xl border border-imovPrimary bg-white px-5 py-2 text-sm font-semibold text-imovPrimary hover:bg-imovPrimary/5 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    {t('account.mySpace.seeListing')}
                  </button>
                </div>
              </li>
            )
          })}
        </ul>
      )}
    </section>
  )
  }
)

MySpaceApplicationsSection.displayName = 'MySpaceApplicationsSection'

export default MySpaceApplicationsSection
